import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCarSide,
  faClock,
  faHandHoldingHeart,
  faHospital,
  faLocationDot,
  faPlane,
  faPhone,
  faShieldAlt,
  faWallet,
  faWheelchair
} from '@fortawesome/free-solid-svg-icons';
import Banner from './Banner';
import pricingRates from '../data/pricing.json';
import '../styles/Services.css';
import { useTranslation } from 'react-i18next';

function Services() {
  const { t, i18n } = useTranslation();
  const reducedMotion = useReducedMotion();
  const isDutch = i18n.resolvedLanguage?.startsWith('nl');
  const amount = (value) => `${new Intl.NumberFormat(isDutch ? 'nl-BE' : 'fr-BE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)} €`;
  const reveal = reducedMotion ? {} : {
    initial: { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.18 }
  };
  const services = [
    {
      icon: faWheelchair,
      tag: isDutch ? 'Toegankelijke mobiliteit' : 'Mobilité accessible',
      title: t('translate.services_pmr_title'),
      description: t('translate.services_pmr_desc'),
      items: [1, 2, 3, 4].map((item) => t(`translate.services_pmr_item${item}`))
    },
    {
      icon: faHandHoldingHeart,
      tag: isDutch ? 'Menselijke aanwezigheid' : 'Présence humaine',
      title: t('translate.services_accompagnement_title'),
      description: t('translate.services_accompagnement_desc'),
      items: [1, 2, 3, 4].map((item) => t(`translate.services_accompagnement_item${item}`))
    },
    {
      icon: faClock,
      tag: isDutch ? '7 dagen per week' : '7 jours sur 7',
      title: t('translate.services_disponibilite_title'),
      description: t('translate.services_disponibilite_desc'),
      items: [1, 2, 3, 4].map((item) => t(`translate.services_disponibilite_item${item}`))
    }
  ];
  const serviceGuides = isDutch ? [
    ['/transport-medico-sanitaire-bruxelles', faCarSide, 'Medisch vervoer', 'Gepland patiëntenvervoer naar zorg en raadplegingen.'],
    ['/transport-pmr-bruxelles', faWheelchair, 'Aangepast rolstoelvervoer', 'Vervoer voor personen met beperkte mobiliteit.'],
    ['/transport-dialyse-bruxelles', faClock, 'Dialysevervoer', 'Terugkerende ritten voor dialyse en regelmatige zorg.'],
    ['/transport-hopital-bruxelles', faHospital, 'Ziekenhuisvervoer', 'Van thuis naar het ziekenhuis en terug.'],
    ['/transport-seniors-bruxelles', faHandHoldingHeart, 'Seniorenvervoer', 'Begeleiding vanaf woning of woonzorgcentrum.'],
    ['/remboursement-transport-medical', faWallet, 'Terugbetaling', 'Informatie over mutualiteit, Partenamut en OCMW.'],
    ['/transport-medical-anderlecht', faLocationDot, 'Anderlecht en Brussel', 'Vervoer in de 19 Brusselse gemeenten.']
  ] : [
    ['/transport-medico-sanitaire-bruxelles', faCarSide, 'Transport médico-sanitaire', 'VSL et transport non urgent vers les lieux de soins.'],
    ['/transport-pmr-bruxelles', faWheelchair, 'Transport PMR et TPMR', 'Véhicule adapté et transport en fauteuil roulant.'],
    ['/transport-dialyse-bruxelles', faClock, 'Transport dialyse', 'Trajets récurrents pour la dialyse et les soins réguliers.'],
    ['/transport-hopital-bruxelles', faHospital, 'Transport hôpital', 'Du domicile à l’hôpital, avec retour organisé.'],
    ['/transport-seniors-bruxelles', faHandHoldingHeart, 'Transport seniors', 'Accompagnement depuis le domicile ou une maison de repos.'],
    ['/remboursement-transport-medical', faWallet, 'Remboursement du transport', 'Mutuelle, Partenamut, CPAS et agrément COCOF.'],
    ['/transport-medical-anderlecht', faLocationDot, 'Anderlecht et Bruxelles', 'Service dans les 19 communes de Bruxelles.']
  ];
  const pricing = isDutch ? {
    eyebrow: 'Officiële tarieven',
    title: 'Duidelijke prijzen, zonder verrassingen',
    intro: 'Bekijk de tarieven voor medisch-sanitair, niet-medisch en luchthavenvervoer.',
    baseBadge: 'Basistarief', perTrip: 'per rit', perKm: 'per km',
    base: 'Licht sanitair voertuig', baseText: 'Basistarief voor een rit, vóór eventuele bijkomende kilometers.',
    nonMedical: 'Niet-medisch vervoer', nonMedicalText: 'In Brussel. Buiten Brussel: 2,50 € per km, met een minimum van 45 € per rit.',
    airport: 'Luchthaven', airportText: 'Forfait vanuit het Brussels Hoofdstedelijk Gewest naar Brussels Airport.',
    night: 'Nachtprestatie', nightText: 'Tarief voor een ophaling tussen 20 u en 6 u.',
    tierOne: 'Van de 11e tot de 30e km', tierOneText: 'Tarief per bijkomende kilometer in deze afstandsschijf.',
    tierTwo: 'Vanaf de 31e km', tierTwoText: 'Tarief per bijkomende kilometer vanaf de 31e kilometer.',
    distanceDetails: 'Kilometertoeslagen', included: 'Van de 1e tot en met de 10e km', includedValue: 'Inbegrepen in het basistarief', from11: 'Van de 11e tot de 30e km', from31: 'Vanaf de 31e km',
    schedule: 'Tarief volgens het uur', dayTime: 'Ophaling tussen 6 u en 20 u', nightTime: 'Ophaling tussen 20 u en 6 u',
    cancellations: 'Annuleringen', before: 'Minder dan 2 uur voor de afspraak',
    refundTitle: 'Verlaag het bedrag van uw factuur', refundText: 'Afhankelijk van uw situatie kunnen uw mutualiteit of het OCMW een deel van het vervoer terugbetalen. Informeer naar de voorwaarden en documenten.',
    estimate: 'Mijn rit ramen', partner: 'Vervoer bij Partenamut'
  } : {
    eyebrow: 'Tarifs officiels',
    title: 'Des prix clairs, sans surprise',
    intro: 'Retrouvez les tarifs applicables aux transports médico-sanitaires, non médico-sanitaires et aéroport.',
    baseBadge: 'Tarif de base', perTrip: 'par trajet', perKm: 'par km',
    base: 'Véhicule sanitaire léger', baseText: 'Tarif de base d’un trajet, avant les éventuels kilomètres supplémentaires.',
    nonMedical: 'Non médico-sanitaire', nonMedicalText: 'Dans Bruxelles. Hors Bruxelles : 2,50 € par km, avec un minimum de 45 € par trajet.',
    airport: 'Aéroport', airportText: 'Forfait au départ de la Région de Bruxelles-Capitale vers Bruxelles-National.',
    night: 'Prestation de nuit', nightText: 'Tarif applicable pour une prise en charge entre 20 h et 6 h.',
    tierOne: 'Du 11e au 30e km', tierOneText: 'Tarif par kilomètre supplémentaire dans cette tranche.',
    tierTwo: 'À partir du 31e km', tierTwoText: 'Tarif par kilomètre supplémentaire dès le 31e kilomètre.',
    distanceDetails: 'Suppléments kilométriques', included: 'Du 1er au 10e km', includedValue: 'Inclus dans le tarif de base', from11: 'Du 11e au 30e km', from31: 'À partir du 31e km',
    schedule: 'Tarif selon l’horaire', dayTime: 'Prise en charge entre 6 h et 20 h', nightTime: 'Prise en charge entre 20 h et 6 h',
    cancellations: 'Annulations', before: 'Moins de 2 h avant le rendez-vous',
    refundTitle: 'Réduisez le montant de votre facture', refundText: 'Selon votre situation, votre mutualité ou le CPAS peuvent rembourser la totalité ou une partie du transport. Renseignez-vous sur les conditions et les documents à fournir.',
    estimate: 'Estimer mon trajet', partner: 'Transport chez Partenamut'
  };

  return (
    <div>
      <Banner 
        title={t("translate.services_title")}
        secondaryTitle={isDutch
          ? <>Een dienst voor <span className="banner-secondary-accent">elke verplaatsing</span></>
          : <>Un service <span className="banner-secondary-accent">pensé</span> pour chaque trajet</>}
        subtitle={t("translate.services_subtitle")}
        className="services-banner"
      />

      <div className="services-content">
        <motion.div className="inner-section-heading centered service-points-heading" {...reveal} transition={{ duration: 0.5 }}>
          <span><FontAwesomeIcon icon={faHandHoldingHeart} />{isDutch ? 'Drie sterke punten' : 'Trois points forts'}</span>
        </motion.div>
        <section className="service-section">
          {services.map((service, index) => (
            <motion.article
              className={`service-card service-card-${index + 1}`}
              key={service.title}
              {...reveal}
              transition={{ duration: 0.5, delay: index * 0.08 }}
            >
              <div className="service-card-topline">
                <div className="service-icon"><FontAwesomeIcon icon={service.icon} /></div>
                <span className="service-card-index">0{index + 1}</span>
              </div>
              <span className="service-card-tag">{service.tag}</span>
              <h2>{service.title}</h2>
              <p>{service.description}</p>
              <ul>{service.items.map((item) => <li key={item}>{item}</li>)}</ul>
            </motion.article>
          ))}
        </section>

        <section className="service-guides-section" aria-labelledby="service-guides-title">
          <motion.div className="inner-section-heading" {...reveal} transition={{ duration: 0.5 }}>
            <span><FontAwesomeIcon icon={faCarSide} />{isDutch ? 'Kies uw vervoer' : 'Choisissez votre transport'}</span>
            <h2 id="service-guides-title">{isDutch ? 'Praktische informatie per behoefte' : 'Une réponse précise pour chaque besoin'}</h2>
            <p>{isDutch
              ? 'Bekijk de praktische informatie voor uw bestemming, mobiliteit en mogelijke tussenkomst.'
              : 'Consultez les informations utiles selon votre destination, votre mobilité et les possibilités de prise en charge.'}</p>
          </motion.div>
          <nav className="service-guides-grid" aria-label={isDutch ? 'Vervoersdiensten' : 'Services de transport'}>
            {serviceGuides.map(([path, icon, title, description], index) => (
              <motion.div key={path} {...reveal} transition={{ duration: 0.45, delay: index * 0.04 }}>
                <Link to={path}>
                  <span className="service-guide-icon"><FontAwesomeIcon icon={icon} /></span>
                  <span className="service-guide-copy"><strong>{title}</strong><small>{description}</small></span>
                  <FontAwesomeIcon className="service-guide-arrow" icon={faArrowRight} />
                </Link>
              </motion.div>
            ))}
          </nav>
        </section>

        <section className="pricing-section">
          <motion.div className="inner-section-heading centered" {...reveal} transition={{ duration: 0.5 }}>
            <span><FontAwesomeIcon icon={faShieldAlt} />{pricing.eyebrow}</span>
            <h2>{pricing.title}</h2>
            <p>{pricing.intro}</p>
          </motion.div>
          <div className="pricing-primary-grid">
            <motion.article className="rate-card rate-card-featured" {...reveal} transition={{ duration: 0.5 }}>
              <span className="rate-badge"><FontAwesomeIcon icon={faHandHoldingHeart} />{pricing.baseBadge}</span>
              <h3>{pricing.base}</h3>
              <p className="rate-price">{amount(pricingRates.baseRate)} <small>{pricing.perTrip}</small></p>
              <p>{pricing.baseText}</p>
            </motion.article>
            <motion.article className="rate-card" {...reveal} transition={{ duration: 0.5, delay: 0.05 }}>
              <FontAwesomeIcon className="rate-card-icon" icon={faCarSide} />
              <h3>{pricing.nonMedical}</h3>
              <p className="rate-price">{amount(pricingRates.nonMedicalRate)} <small>{pricing.perTrip}</small></p>
              <p>{pricing.nonMedicalText}</p>
            </motion.article>
            <motion.article className="rate-card" {...reveal} transition={{ duration: 0.5, delay: 0.1 }}>
              <FontAwesomeIcon className="rate-card-icon" icon={faPlane} />
              <h3>{pricing.airport}</h3>
              <p className="rate-price">{amount(pricingRates.airportRate)} <small>{pricing.perTrip}</small></p>
              <p>{pricing.airportText}</p>
            </motion.article>
            <motion.article className="rate-card" {...reveal} transition={{ duration: 0.5, delay: 0.15 }}>
              <FontAwesomeIcon className="rate-card-icon" icon={faClock} />
              <h3>{pricing.night}</h3>
              <p className="rate-price">{amount(pricingRates.nightRate)} <small>{pricing.perTrip}</small></p>
              <p>{pricing.nightText}</p>
            </motion.article>
            <motion.article className="rate-card" {...reveal} transition={{ duration: 0.5, delay: 0.2 }}>
              <h3>{pricing.tierOne}</h3>
              <p className="rate-price">{amount(pricingRates.kmRate11To30)} <small>{pricing.perKm}</small></p>
              <p>{pricing.tierOneText}</p>
            </motion.article>
            <motion.article className="rate-card" {...reveal} transition={{ duration: 0.5, delay: 0.25 }}>
              <h3>{pricing.tierTwo}</h3>
              <p className="rate-price">{amount(pricingRates.kmRateFrom31)} <small>{pricing.perKm}</small></p>
              <p>{pricing.tierTwoText}</p>
            </motion.article>
          </div>

          <div className="pricing-detail-grid">
            <motion.article className="rate-detail-card" {...reveal} transition={{ duration: 0.5 }}>
              <h3>{pricing.distanceDetails}</h3>
              <div><span>{pricing.included}</span><strong>{pricing.includedValue}</strong></div>
              <div><span>{pricing.from11}</span><strong>{amount(pricingRates.kmRate11To30)} / km</strong></div>
              <div><span>{pricing.from31}</span><strong>{amount(pricingRates.kmRateFrom31)} / km</strong></div>
            </motion.article>
            <motion.article className="rate-detail-card" {...reveal} transition={{ duration: 0.5, delay: 0.06 }}>
              <h3>{pricing.schedule}</h3>
              <div><span>{pricing.dayTime}</span><strong>{amount(pricingRates.baseRate)}</strong></div>
              <div><span>{pricing.nightTime}</span><strong>{amount(pricingRates.nightRate)}</strong></div>
            </motion.article>
            <motion.article className="rate-detail-card" {...reveal} transition={{ duration: 0.5, delay: 0.12 }}>
              <h3>{pricing.cancellations}</h3>
              <div><span>{pricing.before}</span><strong>{amount(pricingRates.lateCancellation)}</strong></div>
            </motion.article>
          </div>

          <motion.div className="pricing-refund" {...reveal} transition={{ duration: 0.5 }}>
            <FontAwesomeIcon icon={faWallet} />
            <div><h3>{pricing.refundTitle}</h3><p>{pricing.refundText}</p></div>
            <div className="pricing-refund-actions">
              <Link to="/reservation">{pricing.estimate}<FontAwesomeIcon icon={faArrowRight} /></Link>
              <a href="https://www.partenamut.be/fr/services-ma-mutuelle/transport-medical-non-urgent" target="_blank" rel="noreferrer">{pricing.partner}<FontAwesomeIcon icon={faArrowRight} /></a>
            </div>
          </motion.div>
        </section>

        <motion.section className="booking-section" {...reveal} transition={{ duration: 0.55 }}>
          <div className="booking-copy">
            <span>{isDutch ? 'Uw volgende rit' : 'Votre prochain trajet'}</span>
            <h2>{t("translate.services_booking_title")}</h2>
            <p>{t("translate.services_booking_desc")}</p>
          </div>
          <div className="booking-buttons">
            <Link to="/reservation" className="booking-button primary">{t("translate.services_booking_button")}<FontAwesomeIcon icon={faArrowRight} /></Link>
            <a href="tel:+32491333777" className="booking-button secondary">
              <FontAwesomeIcon icon={faPhone} /> +32 491 33 37 77
            </a>
          </div>
        </motion.section>
      </div>
    </div>
  );
}

export default Services;
